import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useFetch } from "../../../hooks/useFetch";
import { isHttpError } from "../../../errors/isApiError";
import { useUserContext } from "../../../contexts/UserContext";
import { sha256Hex } from "../../../utils/hash";
import { createVerificationSession } from "../services/sessionService";

// The verification flow is a cross-origin iframe, so its content height can't be read
// from the parent page. Give it a generous fixed height and let the page scroll instead
// of constraining it to the viewport, which would force a tiny internal scrollbar.
const IFRAME_HEIGHT = 1000;

function VerificationPage() {
  const navigate = useNavigate();
  const { userInfo } = useUserContext();
  const [searchParams] = useSearchParams();
  const {
    data: session,
    isLoading,
    error,
    fetchData: startSession,
  } = useFetch(createVerificationSession);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }
    hasStarted.current = true;

    async function start() {
      const profileCode = searchParams.get("profileCode");
      const jurisdiction = searchParams.get("jurisdiction");

      // Hash the user's email (plus profile/jurisdiction, when supplied) into
      // a stable applicantReference so the same person requesting the same
      // profile/jurisdiction always maps to the same applicant record with
      // the KYC provider, instead of a fresh random id (and a "new
      // applicant" every time) on every visit. A different profileCode or
      // jurisdiction for the same email intentionally hashes to a different
      // reference, since it's a distinct verification context. Falls back to
      // a random id only when there's no email to key off (e.g. Guest).
      const applicantReference = userInfo?.email
        ? await sha256Hex(
            [userInfo.email.trim().toLowerCase(), profileCode, jurisdiction]
              .filter(Boolean)
              .join("|")
          )
        : `demo_ref_${crypto.randomUUID()}`;

      // Caller can pin a specific profile/jurisdiction via query params
      // (e.g. a link like /verification?profileCode=...&jurisdiction=...);
      // sessionService falls back to the default profile when absent.
      await startSession(applicantReference, { profileCode, jurisdiction });
    }

    start().catch(() => {
      // Surfaced via the error state below.
    });
  }, [startSession, userInfo, searchParams]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
          onClick={() => navigate("/")}
        >
          Back
        </Button>
        <Typography variant="h6" fontWeight={700}>
          Start Verification
        </Typography>
      </Stack>

      {isLoading && (
        <Paper
          variant="outlined"
          sx={{
            height: IFRAME_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Starting your verification session…
            </Typography>
          </Stack>
        </Paper>
      )}

      {error != null && (
        <Alert severity="error">
          {isHttpError(error)
            ? `Could not start the verification session: ${error.message}`
            : "Could not start the verification session. Please try again."}
        </Alert>
      )}

      {session?.formUrl != null && (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Box
            component="iframe"
            src={session.formUrl}
            title="KYC Verification"
            allow="camera; microphone"
            sx={{ width: "100%", height: IFRAME_HEIGHT, border: "none", display: "block" }}
          />
        </Paper>
      )}
    </Stack>
  );
}

export default VerificationPage;
